import { ReactNode, createContext, useEffect, useRef } from 'react';
import { createID, Redline } from 'redline-client-sdk';

import { RLContext } from '../types';
import { autoEvents } from '../events/auto';
import { ecommerceEvents } from '../events/ecommerce';
import { experienceEvents } from '../events/experience';
import { sortingHatEvents } from '../events/sortingHat';
import { userTrackingEvents } from '../events/userTracking';
import { userAuthenticationEvents } from '../events/userAuthentication';
import { dogxEvents } from '../events/dogx';
import { debtsEvents } from '../events/debts';
import { atendeEvents } from '../events/atende';
import { mobileEvents } from '../events/mobile';
import { capraEvents } from '../events/capra';
import { MatchMakerEvents } from '../events/matchmaker';
import { renegkitEvents } from '../events/renegkit';
import { addCookie, getCookie } from '@libs/utils/helpers/getSafeCookies';

import { usePixel } from '../hooks/usePixel';

const RedlineContext = createContext<RLContext>({} as RLContext);
interface IRedlineProvider {
  children: ReactNode;
  appName?: string;
  instance: Redline;
}

const RedlineProvider: React.FC<IRedlineProvider> = ({ children, appName, instance }) => {
  const redlineInstance = useRef(instance);
  const { startPixel } = usePixel();

  useEffect(() => {
    redlineInstance.current.start();

    if (appName?.includes('cp')) {
      startPixel('cp');
    } else {
      startPixel('ac');
    }
  }, []);

  const events = {
    track: {
      auto: autoEvents(redlineInstance.current),
      ecommerce: ecommerceEvents(redlineInstance.current),
      experience: experienceEvents(redlineInstance.current),
      sortingHat: sortingHatEvents(redlineInstance.current),
      userTracking: userTrackingEvents(redlineInstance.current),
      userAuthentication: userAuthenticationEvents(redlineInstance.current, appName),
      debts: debtsEvents(redlineInstance.current),
      atende: atendeEvents(redlineInstance.current),
      dogx: dogxEvents(redlineInstance.current),
      mobile: mobileEvents(redlineInstance.current),
      capra: capraEvents(redlineInstance.current),
      renegkit: renegkitEvents(redlineInstance.current),
      matchmaker: MatchMakerEvents(redlineInstance.current),
      experimental: (name: string, payload: Record<string, any>) => {
        const event = `experimental.${name}.v1` as const;
        return redlineInstance.current.track(event, { event, ...payload });
      },
    },
  };

  function createCorrelationId(key: string) {
    const correlationId = createID();
    const thirtyMinutes = new Date();
    const getCurrentId = getCookie(key);

    if (!getCurrentId) {
      thirtyMinutes.setTime(thirtyMinutes.getTime() + 30 * 60 * 1000);

      addCookie({
        key,
        value: correlationId,
        expires: thirtyMinutes,
      });
    }
  }

  function getCorrelationId(key: string) {
    return getCookie(key);
  }

  function endSession() {
    /* redlineInstance.current.endSession(); */
  }

  return <RedlineContext.Provider value={{ ...events, createCorrelationId, getCorrelationId, endSession }}>{children}</RedlineContext.Provider>;
};

export { RedlineProvider, RedlineContext };
